import type { FormState, UserTypeConfig, UserType } from '@/shared/types/users.types';
import { isDriverUserType } from '@/shared/types/users.types';

export function StepConsents({
    form,
    set,
    config,
    userType,
}: {
    form: FormState;
    set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
    config: UserTypeConfig;
    userType: string;
}) {
    const isDriver = isDriverUserType(userType as UserType);
    const name = form.firstName ? `${form.firstName} ${form.lastName}`.trim() : '—';

    const checkboxes = [
        {
            key: 'acceptTerms' as const,
            text: "J'accepte les conditions générales d'utilisation",
            required: true,
            icon: 'ri-file-text-line',
        },
        {
            key: 'acceptPrivacy' as const,
            text: "J'accepte la politique de confidentialité",
            required: true,
            icon: 'ri-shield-check-line',
        },
        ...(isDriver ? [
            {
                key: 'acceptVerification' as const,
                text: "J'autorise la vérification de mes documents",
                required: true,
                icon: 'ri-folder-check-line',
            },
            {
                key: 'acceptQuality' as const,
                text: "Je m'engage à respecter les délais et normes de qualité",
                required: true,
                icon: 'ri-award-line',
            },
        ] : []),
        {
            key: 'acceptNotifications' as const,
            text: 'Recevoir les notifications par SMS et e-mail',
            required: false,
            icon: 'ri-notification-3-line',
        },
    ];

    const summaryItems = [
        { icon: 'ri-user-star-line', label: 'Profil', value: config.label },
        ...(name !== '—' ? [{ icon: 'ri-user-line', label: 'Nom', value: name }] : []),
        ...(form.email ? [{ icon: 'ri-mail-line', label: 'E-mail', value: form.email }] : []),
        ...(form.phone ? [{
            icon: 'ri-phone-line',
            label: 'Téléphone',
            value: `${form.phoneCountryCode} ${form.phone}`,
        }] : []),
    ];

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold text-gray-900">Validation finale</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Vérifiez vos informations puis acceptez les conditions pour créer votre compte.
                </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-teal-50/40 border border-slate-100 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center">
                        <i className="ri-clipboard-line text-base" />
                    </div>
                    <p className="font-semibold text-gray-900">Récapitulatif</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {summaryItems.map(item => (
                        <div key={item.label} className="flex items-start gap-2.5 bg-white/80 rounded-xl p-3 border border-white">
                            <i className={`${item.icon} text-teal-600 mt-0.5`} />
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{item.label}</p>
                                <p className="text-sm font-medium text-gray-800 truncate">{item.value}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-3">Consentements</p>
                {checkboxes.map(cb => (
                    <label
                        key={cb.key}
                        className={`flex items-start gap-3 cursor-pointer p-3.5 rounded-xl border transition-all ${
                            form[cb.key]
                                ? 'border-teal-200 bg-teal-50/50'
                                : 'border-gray-100 bg-white hover:border-gray-200'
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={form[cb.key] as boolean}
                            onChange={e => set(cb.key, e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <div className="flex items-start gap-2 min-w-0">
                            <i className={`${cb.icon} text-gray-400 mt-0.5 shrink-0`} />
                            <span className="text-sm text-gray-700 leading-snug">
                                {cb.text}
                                {cb.required && <span className="text-red-500 ml-1">*</span>}
                            </span>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
}
